/**
 * EmbeddedCommandRouter — Host → Efflux (comandos)
 * ================================================
 *
 * Recebe o envelope cru do host, valida (nunca confia cego — R2.3), e roteia
 * cada comando pra ação nos {@link EmbeddedCommandHandlers}.
 *
 * Handshake `ready` (R2.1): comandos que chegam ANTES do player estar pronto
 * ficam numa FILA e são drenados em ordem no `markReady()`. `setBounds` é aceito
 * mas ignorado na Fase 2 (no Android quem move é a View nativa — Fase 3).
 *
 * Dedup por `id` (R2.3/idempotência): um mesmo envelope reentregue não roda 2×.
 */
import { EmbeddedCommandHandlers } from './commandHandlers';
import { CommandEnvelope, parseCommandEnvelope } from './protocol';

export class EmbeddedCommandRouter {
  private ready = false;
  private readonly queue: CommandEnvelope[] = [];
  private readonly seenIds = new Set<string>();

  /** Cap defensivo da fila pré-ready (host malcomportado). */
  private static readonly MAX_QUEUE = 100;
  /** Cap do set de dedup (evita crescer sem limite em runs longos). */
  private static readonly MAX_SEEN = 512;

  constructor(private readonly handlers: EmbeddedCommandHandlers) {}

  /**
   * Ponto de entrada do transporte (`window.__efflux.dispatch`). Aceita string
   * JSON ou objeto já parseado. Silencioso e seguro em entrada malformada.
   */
  dispatch(raw: unknown): void {
    const env = parseCommandEnvelope(raw);
    if (!env) {
      console.debug(
        '[EmbeddedCommandRouter] envelope de comando inválido — ignorado',
      );
      return;
    }

    if (this.seenIds.has(env.id)) {
      console.debug(
        '[EmbeddedCommandRouter] comando duplicado ignorado:',
        env.id,
      );
      return;
    }
    this.rememberId(env.id);

    if (!this.ready) {
      if (this.queue.length >= EmbeddedCommandRouter.MAX_QUEUE) {
        this.queue.shift();
      }
      this.queue.push(env);
      return;
    }
    void this.route(env);
  }

  /** Player pronto: drena a fila em ordem e passa a rotear ao vivo. Idempotente. */
  markReady(): void {
    if (this.ready) return;
    this.ready = true;
    const pending = this.queue.splice(0, this.queue.length);
    for (const env of pending) {
      void this.route(env);
    }
  }

  isReady(): boolean {
    return this.ready;
  }

  queuedCount(): number {
    return this.queue.length;
  }

  private async route(env: CommandEnvelope): Promise<void> {
    try {
      switch (env.type) {
        case 'start':
          await this.handlers.start(env.payload as never);
          break;
        case 'stop':
          await this.handlers.stop();
          break;
        case 'configure':
          await this.handlers.configure(env.payload as never);
          break;
        case 'hide':
          await this.handlers.hide();
          break;
        case 'unhide':
          await this.handlers.unhide();
          break;
        case 'setVolume':
          await this.handlers.setVolume(env.payload as never);
          break;
        case 'releaseVolume':
          await this.handlers.releaseVolume();
          break;
        case 'reload':
          await this.handlers.reload();
          break;
        case 'captureCoverFrame':
          await this.handlers.captureCoverFrame();
          break;
        case 'setBounds':
          // Fase 2: no Android quem move é a View nativa (Fase 3 usa de verdade).
          break;
      }
    } catch (err) {
      console.debug('[EmbeddedCommandRouter] handler falhou:', env.type, err);
    }
  }

  private rememberId(id: string): void {
    this.seenIds.add(id);
    if (this.seenIds.size > EmbeddedCommandRouter.MAX_SEEN) {
      // Descarta o mais antigo inserido (Set mantém ordem de inserção).
      const oldest = this.seenIds.values().next().value;
      if (oldest !== undefined) this.seenIds.delete(oldest);
    }
  }
}
